import { useState, type InputHTMLAttributes } from "react";
import { formatMoney, parseMoneyInput, penceToPounds } from "@finances/shared/money";
import { Input } from "./Field.js";

interface MoneyInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
> {
  /** Integer pence, or null when the field is empty. */
  value: number | null;
  onValueChange: (pence: number | null) => void;
}

function displayFor(pence: number | null): string {
  return pence == null ? "" : penceToPounds(pence).toFixed(2);
}

/**
 * The only place a string becomes money on the client — which is exactly how
 * many places that should be.
 *
 * Accepts what a person actually types (`1,234.50`, `£1234.5`, `1234`) and
 * emits integer pence.
 *
 * While the field is focused it holds a draft string, so typing "1." is not
 * reformatted out from under the cursor. Blur discards the draft and the
 * displayed text goes back to being derived from the value, which means the
 * two can never drift.
 */
export function MoneyInput({ value, onValueChange, onFocus, onBlur, ...rest }: MoneyInputProps) {
  const [draft, setDraft] = useState<string | null>(null);

  const text = draft ?? displayFor(value);
  const parsed = text.trim() === "" ? null : parseMoneyInput(text);
  const unparseable = text.trim() !== "" && parsed === null;

  return (
    <Input
      inputMode="decimal"
      autoComplete="off"
      numeric
      invalid={unparseable}
      value={text}
      onFocus={(event) => {
        setDraft(displayFor(value));
        onFocus?.(event);
      }}
      onChange={(event) => {
        const next = event.target.value;
        setDraft(next);
        const pence = next.trim() === "" ? null : parseMoneyInput(next);
        // An unparseable string is not a value change: the field shows it as
        // invalid rather than silently reporting the last good number.
        if (next.trim() === "" || pence !== null) onValueChange(pence);
      }}
      onBlur={(event) => {
        setDraft(null);
        onBlur?.(event);
      }}
      title={parsed !== null ? formatMoney(parsed) : undefined}
      {...rest}
    />
  );
}
